import React from "react";
import { CHART_COLORS } from "@/features/investors/components/charts/palette";
import {
    CardHeader,
    InsightHtml,
    MetricText,
    Micro,
    SectionHead,
} from "@/features/investors/components/atoms/InvestorsAtoms";
import { InvestorsDashboardData, LegDuration } from "@/features/investors/types/investors.types";
import { InvestorsInsights } from "@/features/investors/utils/investors-insights";
import { investorsFormatters as fmt } from "@/features/investors/utils/investors-formatters";

const LEG_COLORS: Record<LegDuration["leg"], string> = {
    inflow: CHART_COLORS.blue,
    exchange: CHART_COLORS.violet,
    outflow: CHART_COLORS.teal,
};

const PROVIDER_DOT_COLORS = [
    CHART_COLORS.blue,
    CHART_COLORS.teal,
    CHART_COLORS.violet,
    CHART_COLORS.lime,
    CHART_COLORS.orange,
    CHART_COLORS.pink,
    CHART_COLORS.gold,
];

type Props = {
    data: InvestorsDashboardData;
    insights: InvestorsInsights;
};

export const OperationsSection: React.FC<Props> = ({ data, insights }) => {
    const { operations } = data;
    const measurableLegs = operations.legs.filter(
        (leg) => leg.status === "ok" && leg.p50Ms !== null && leg.p50Ms > 0
    );
    const lifecycleTotal = measurableLegs.reduce((total, leg) => total + (leg.p50Ms ?? 0), 0);

    return (
        <section className="sec" id="sec-ops">
            <SectionHead
                index="06"
                title="Operational scalability"
                question={
                    <>
                        Question: <em>Can we scale without adding people?</em>
                    </>
                }
            />
            <div className="grid" style={{ gridTemplateColumns: "repeat(3,1fr)", marginBottom: 20 }}>
                <Micro
                    label="Automation rate"
                    value={
                        <MetricText metric={operations.automationRatePct} render={(value) => fmt.pct(value)} />
                    }
                    valueColor="var(--pos)"
                    sub="Completed with no manual intervention"
                />
                <Micro
                    label="Same-day completion"
                    value={
                        <MetricText metric={operations.sameDayRatePct} render={(value) => fmt.pct(value)} />
                    }
                    sub="Completed within 24h of starting"
                />
                <Micro
                    label="Manual interventions"
                    value={<MetricText metric={operations.manualRatePct} render={(value) => fmt.pct(value)} />}
                    sub={
                        operations.operationalLeverage.value !== null
                            ? `Leverage: ${operations.operationalLeverage.value.toFixed(1)} ops per manual op`
                            : "Operational leverage unavailable"
                    }
                />
            </div>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <div className="card">
                    <CardHeader
                        title="Operation lifecycle"
                        sub="Median (p50) time across the measured legs"
                        tag={
                            operations.duration.p50Ms !== null
                                ? `p50 ${fmt.durationMs(operations.duration.p50Ms)}`
                                : undefined
                        }
                    />
                    {lifecycleTotal > 0 ? (
                        <div
                            style={{
                                display: "flex",
                                height: 42,
                                borderRadius: 10,
                                overflow: "hidden",
                                border: "1px solid var(--hair)",
                            }}
                        >
                            {measurableLegs.map((leg) => {
                                const p50 = leg.p50Ms ?? 0;
                                const share = (p50 / lifecycleTotal) * 100;
                                return (
                                    <div
                                        key={leg.leg}
                                        data-tip={`<b>${leg.leg}</b><br>p50 ${fmt.durationMs(p50)} · ${Math.round(share)}% of lifecycle`}
                                        style={{
                                            width: `${share}%`,
                                            background: LEG_COLORS[leg.leg],
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#06121A",
                                            fontWeight: 600,
                                            fontSize: 12,
                                            cursor: "default",
                                        }}
                                    >
                                        {leg.leg} · {fmt.durationMs(p50)}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{ color: "var(--sub)", fontSize: 13 }}>
                            Per-leg durations are not populated for this period.
                        </div>
                    )}
                    {operations.legs.some((leg) => leg.status === "unavailable") ? (
                        <div className="chart-foot" style={{ border: 0, paddingTop: 10 }}>
                            <span className="ck" style={{ fontSize: 11.5, color: "var(--sub)" }}>
                                {operations.legs
                                    .filter((leg) => leg.status === "unavailable")
                                    .map((leg) => leg.leg)
                                    .join(", ")}{" "}
                                leg durations are not recorded in the read index
                            </span>
                        </div>
                    ) : null}
                    <div className="micro-grid" style={{ marginTop: 18 }}>
                        <Micro
                            label="p50"
                            value={
                                operations.duration.p50Ms !== null
                                    ? fmt.durationMs(operations.duration.p50Ms)
                                    : "—"
                            }
                        />
                        <Micro
                            label="p90"
                            value={
                                operations.duration.p90Ms !== null
                                    ? fmt.durationMs(operations.duration.p90Ms)
                                    : "—"
                            }
                        />
                        <Micro
                            label="p99"
                            value={
                                operations.duration.p99Ms !== null
                                    ? fmt.durationMs(operations.duration.p99Ms)
                                    : "—"
                            }
                        />
                    </div>
                </div>
                <div className="card">
                    <CardHeader title="Provider scorecard" sub="Completed operations by provider and leg" />
                    <table className="ptable">
                        <thead>
                            <tr>
                                <th>Provider</th>
                                <th>Leg</th>
                                <th style={{ textAlign: "right" }}>Ops</th>
                                <th style={{ textAlign: "right" }}>Revenue</th>
                                <th style={{ textAlign: "right" }}>p90</th>
                                <th style={{ textAlign: "right" }}>Manual</th>
                            </tr>
                        </thead>
                        <tbody>
                            {operations.providers.map((provider, index) => (
                                <tr key={`${provider.leg}-${provider.provider}`}>
                                    <td>
                                        <span className="pname">
                                            <span
                                                className="pdot"
                                                style={{
                                                    background:
                                                        PROVIDER_DOT_COLORS[index % PROVIDER_DOT_COLORS.length],
                                                }}
                                            />
                                            {provider.provider}
                                        </span>
                                    </td>
                                    <td style={{ color: "var(--mut)" }}>{provider.leg}</td>
                                    <td style={{ textAlign: "right" }}>{fmt.num(provider.operations)}</td>
                                    <td style={{ textAlign: "right" }}>{fmt.moneyCompact(provider.revenue)}</td>
                                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                        {provider.p90DurationMs !== null
                                            ? fmt.durationMs(provider.p90DurationMs)
                                            : "—"}
                                    </td>
                                    <td style={{ textAlign: "right" }}>
                                        {provider.manualRatePct !== null
                                            ? fmt.pct(provider.manualRatePct)
                                            : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="grid" style={{ gridTemplateColumns: "repeat(3,1fr)", marginTop: 20 }}>
                <Micro
                    label="Slow operations"
                    value={<MetricText metric={operations.slowRatePct} render={(value) => fmt.pct(value)} />}
                    sub="Above the 1h SLA threshold"
                />
                {operations.manualVsAutomated.map((comparison) => (
                    <Micro
                        key={comparison.label}
                        label={`${comparison.label} · p50`}
                        value={comparison.p50Ms !== null ? fmt.durationMs(comparison.p50Ms) : "—"}
                        sub={`${fmt.numShort(comparison.operations)} operations`}
                    />
                ))}
            </div>
            <div style={{ marginTop: 20 }}>
                <InsightHtml html={insights.notes.operations} />
            </div>
        </section>
    );
};
